import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { Character } from '../types/Character';
import { Conversation } from '../types/Conversation';
import { initialCharacters, initialZones } from '../data/initialData';
import {
  fetchCharacters,
  fetchConversations,
  fetchZones,
  updateCharacterPosition,
  updateCharacterStatus as updateCharacterStatusInDB,
  subscribeToCharacters,
  subscribeToConversations,
  dbCharacterToCharacter,
  dbConversationToConversation,
  triggerGameTick,
  autoSeedDatabase,
  DBAgent,
  DBConversation,
  DBZone,
  dbBuildingToBuilding
} from '../services/firebaseClient';
import { Zone } from '../types/Town';

export interface TownState {
  characters: Character[];
  zones: Zone[];
  conversations: Conversation[];
  townClock: number;
  isSimulationRunning: boolean;
  selectedCharacter: Character | null;
  isLoading: boolean;
  dataSource: 'local' | 'firebase';
  setSelectedCharacter: (character: Character | null) => void;
  toggleSimulation?: () => void;
  moveCharacter?: (characterId: string, targetPosition: { x: number, y: number }) => void;
  updateCharacterStatus?: (characterId: string, status: Character['status']) => void;
}

const TownStateContext = createContext<TownState | undefined>(undefined);

export const TownStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Estado
  const [characters, setCharacters] = useState<Character[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [dbConversations, setDbConversations] = useState<DBConversation[]>([]); // Store raw DB data
  const [townClock, setTownClock] = useState<number>(0);
  const [isSimulationRunning, setIsSimulationRunning] = useState<boolean>(true); // Auto-start simulation
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dataSource, setDataSource] = useState<'local' | 'firebase'>('local');

  // Derived state for UI
  const conversations = useMemo(() => {
    return dbConversations.map(c => dbConversationToConversation(c, characters));
  }, [dbConversations, characters]);

  // ============================================================================
  // CARGAR DATOS DESDE FIREBASE
  // ============================================================================
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      // Auto-seed silently if needed
      await autoSeedDatabase();

      // 1. Cargar Personajes y Zonas
      const dbCharacters = await fetchCharacters();
      const dbZones = await fetchZones();

      if (dbCharacters.length > 0) {
        // ✅ Datos desde Firebase
        const chars = dbCharacters.map(dbCharacterToCharacter);
        setCharacters(chars);
        setZones(dbZones.map(dbBuildingToBuilding));
        setDataSource('firebase');
        console.log('✅ Loaded characters from Firebase:', chars.length);

        // 2. Cargar Conversaciones
        const loadedDbConversations = await fetchConversations();
        setDbConversations(loadedDbConversations);
        console.log('✅ Loaded conversations from Firebase:', loadedDbConversations.length);

      } else {
        // ⚠️ Fallback a datos locales (si Firebase falla estrepitosamente)
        setCharacters(initialCharacters);
        setZones(initialZones);
        setDbConversations([]);
        setDataSource('local');
        console.log('⚠️ Using local data (Firebase not available)');
      }

      setIsLoading(false);
    };

    loadData();
  }, []);

  // ============================================================================
  // SUSCRIPCIÓN A REALTIME (PERSONAJES)
  // ============================================================================
  useEffect(() => {
    if (dataSource !== 'firebase') return;

    const handleUpdate = (dbCharacters: DBAgent[]) => {
      const chars = dbCharacters.map(dbCharacterToCharacter);
      setCharacters(chars);
    };

    const unsubscribe = subscribeToCharacters(handleUpdate);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [dataSource]);

  // ============================================================================
  // SUSCRIPCIÓN A REALTIME (CONVERSACIONES)
  // ============================================================================
  useEffect(() => {
    if (dataSource !== 'firebase') return;

    const handleUpdate = (newDbConversations: DBConversation[]) => {
      setDbConversations(newDbConversations);
    };

    const unsubscribe = subscribeToConversations(handleUpdate);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [dataSource]);

  // ============================================================================
  // GAME LOOP
  // ============================================================================
  useEffect(() => {
    if (dataSource !== 'firebase' || !isSimulationRunning) return;

    console.log('🎮 Starting Game Loop (15s interval)...');
    const gameLoopInterval = setInterval(async () => {
      try {
        const result = await triggerGameTick();
        if (!result.success) {
          console.error('❌ Game tick failed:', result.error);
        }
      } catch (err) {
        console.error('❌ Game loop error:', err);
      }
    }, 15000); // 15 seconds between ticks to respect API limits

    return () => clearInterval(gameLoopInterval);
  }, [dataSource, isSimulationRunning]);

  // ============================================================================
  // ACCIONES
  // ============================================================================
  const toggleSimulation = useCallback(() => {
    setIsSimulationRunning(prev => !prev);
  }, []);

  const moveCharacter = useCallback(async (characterId: string, targetPosition: { x: number; y: number }) => {
    setCharacters(prevChars =>
      prevChars.map(char =>
        char.id === characterId
          ? { ...char, position: targetPosition, status: 'moving' as const }
          : char
      )
    );

    if (dataSource === 'firebase') {
      await updateCharacterPosition(characterId, targetPosition.x, targetPosition.y);
    }
  }, [dataSource]);

  const updateCharacterStatus = useCallback(async (characterId: string, status: Character['status']) => {
    setCharacters(prevChars =>
      prevChars.map(char =>
        char.id === characterId ? { ...char, status } : char
      )
    );

    if (dataSource === 'firebase') {
      await updateCharacterStatusInDB(characterId, status);
    }
  }, [dataSource]);

  const value = useMemo(() => ({
    characters,
    zones,
    conversations,
    townClock,
    isSimulationRunning,
    selectedCharacter,
    isLoading,
    dataSource,
    setSelectedCharacter,
    toggleSimulation,
    moveCharacter,
    updateCharacterStatus,
  }), [
    characters,
    zones,
    conversations,
    townClock,
    isSimulationRunning,
    selectedCharacter,
    isLoading,
    dataSource,
    toggleSimulation,
    moveCharacter,
    updateCharacterStatus
  ]);

  return (
    <TownStateContext.Provider value={value}>
      {children}
    </TownStateContext.Provider>
  );
};

export const useTownState = () => {
  const ctx = useContext(TownStateContext);
  if (!ctx) throw new Error('useTownState must be used within TownStateProvider');
  return ctx;
};

export const useTown = useTownState;